import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "focus-ring type-button inline-flex items-center justify-center gap-[var(--control-inline-gap)] whitespace-nowrap rounded-full transition-[background-color,color,border-color,box-shadow,transform] duration-[var(--dur-base)] ease-[var(--ease-standard)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[var(--shadow-1)] hover:bg-primary/92",
        inverse:
          "bg-foreground text-background shadow-[var(--shadow-1)] hover:bg-foreground/92",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[var(--shadow-1)] hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-border-default bg-surface text-foreground shadow-[var(--shadow-0)] hover:bg-accent-soft hover:text-accent-text dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-[var(--shadow-0)] hover:bg-secondary/86",
        ghost:
          "text-muted-foreground hover:bg-accent-soft hover:text-accent-text dark:hover:bg-accent/50",
        link: "rounded-md text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-[var(--control-height-button)] px-[var(--control-padding-x-button)] py-[var(--space-xs)] has-[>svg]:px-[var(--space-sm)]",
        sm: "h-[var(--control-height-button-sm)] gap-[var(--control-inline-gap-sm)] px-[var(--control-padding-x-button-sm)] has-[>svg]:px-[var(--space-xs)]",
        lg: "h-[var(--control-height-button-lg)] px-[var(--control-padding-x-button-lg)] has-[>svg]:px-[var(--space-md)]",
        icon: "size-[var(--control-icon-button)]",
        "icon-sm": "size-[var(--control-icon-button-sm)]",
        "icon-lg": "size-[var(--control-icon-button-lg)]",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
